export const GLOBE_PHI = 0.65;
export const GLOBE_ROLL = -0.25;
export const GLOBE_RADIUS = 0.8;
export const MARKER_ELEVATION = 0.01;

export type GlobePosition = readonly [number, number, number];

export const getGlobePosition = (
  [latitude, longitude]: readonly [number, number],
  radius = GLOBE_RADIUS + MARKER_ELEVATION,
): GlobePosition => {
  const lat = (latitude * Math.PI) / 180;
  const lon = (longitude * Math.PI) / 180;
  return [
    Math.cos(lat) * Math.cos(lon) * radius,
    Math.sin(lat) * radius,
    -Math.cos(lat) * Math.sin(lon) * radius,
  ];
};

export const projectGlobePosition = (
  point: GlobePosition,
  yaw: number,
  width: number,
) => {
  const x = Math.cos(GLOBE_PHI) * point[0] + Math.sin(GLOBE_PHI) * point[2];
  const y = point[1];
  const z = -Math.sin(GLOBE_PHI) * point[0] + Math.cos(GLOBE_PHI) * point[2];
  const rolledX = Math.cos(GLOBE_ROLL) * x - Math.sin(GLOBE_ROLL) * y;
  const rolledY = Math.sin(GLOBE_ROLL) * x + Math.cos(GLOBE_ROLL) * y;
  const rotatedX = Math.cos(yaw) * rolledX + Math.sin(yaw) * z;
  const depth = -Math.sin(yaw) * rolledX + Math.cos(yaw) * z;

  return {
    x: ((rotatedX + 1) * width) / 2,
    y: ((1 - rolledY) * width) / 2,
    visible: depth >= 0 || rotatedX ** 2 + rolledY ** 2 >= GLOBE_RADIUS ** 2,
  };
};

export const getArcControl = (
  from: GlobePosition,
  to: GlobePosition,
  height: number,
): GlobePosition => {
  const midpoint: GlobePosition = [
    from[0] + to[0],
    from[1] + to[1],
    from[2] + to[2],
  ];
  const length = Math.hypot(...midpoint);
  const radius = GLOBE_RADIUS + height + MARKER_ELEVATION;
  if (length < 0.001) {
    return [0, radius, 0];
  }
  return [
    (midpoint[0] / length) * radius,
    (midpoint[1] / length) * radius,
    (midpoint[2] / length) * radius,
  ];
};

export const getArcPosition = (
  from: GlobePosition,
  control: GlobePosition,
  to: GlobePosition,
  progress: number,
): GlobePosition => {
  const remaining = 1 - progress;
  const interpolate = (index: 0 | 1 | 2) =>
    remaining ** 2 * from[index] +
    2 * remaining * progress * control[index] +
    progress ** 2 * to[index];
  return [interpolate(0), interpolate(1), interpolate(2)];
};
